import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Users, BedDouble, Flame, MapPin, Waves, AlertTriangle } from "lucide-react";

export function HutsList() {
  const [huts, setHuts] = useState([]);
  useEffect(() => { api.get("/huts").then(r => setHuts(r.data)); }, []);
  return (
    <div className="max-w-6xl mx-auto px-4 py-10" data-testid="huts-list-page">
      <h1 className="font-serif text-4xl text-emerald-900 mb-8">Les huttes pédagogiques</h1>
      <div className="grid gap-6 md:grid-cols-2">
        {huts.map(h => (
          <Link key={h.id} to={`/huttes/${h.slug}`} className="group bg-white border border-stone-200 rounded-xl overflow-hidden hover:shadow-lg transition" data-testid={`huts-card-${h.slug}`}>
            <div className="aspect-[4/3] bg-stone-200 overflow-hidden">
              <img src={h.photos?.[0] ? fileUrl(h.photos[0]) : ""} alt={h.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-xl text-emerald-900">{h.name}</h2>
                {h.status !== "ACTIVE" && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">Travaux en cours – ouverture prochaine</span>}
              </div>
              <p className="text-sm text-stone-600 mt-2 line-clamp-2">{h.description}</p>
              <div className="mt-3 flex gap-4 text-sm text-stone-600">
                <span className="inline-flex items-center gap-1"><Users size={14}/> {h.max_capacity} pers.</span>
                <span className="inline-flex items-center gap-1"><BedDouble size={14}/> {h.beds} lits</span>
              </div>
              <div className="mt-3 text-emerald-800 font-semibold">{h.price_per_night} € / nuit</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function HutDetail() {
  const { slug } = useParams();
  const [h, setH] = useState(null);
  useEffect(() => { api.get(`/huts/${slug}`).then(r => setH(r.data)).catch(()=>{}); }, [slug]);
  if (!h) return <div className="max-w-4xl mx-auto p-8 text-stone-500">Chargement…</div>;
  const disabled = h.status !== "ACTIVE";
  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid={`hut-detail-${slug}`}>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-serif text-3xl md:text-4xl text-emerald-900">{h.name}</h1>
        {disabled && <span className="text-xs bg-amber-100 text-amber-800 px-3 py-1.5 rounded-md" data-testid="hut-disabled-badge">Travaux en cours – ouverture prochaine</span>}
      </div>

      {h.boat_required && (
        <div className="mb-6 p-4 rounded-md bg-amber-50 border border-amber-200 flex gap-3" data-testid="boat-warning">
          <Waves className="text-amber-700 flex-shrink-0 mt-0.5" size={20}/>
          <div className="text-sm text-amber-900">
            <strong>Accès exclusivement en barque.</strong> L'accès à cette hutte se fait uniquement par barque avec cadenas à code. Assurez-vous d'être en mesure d'utiliser cet accès.
          </div>
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-3 mb-8">
        {(h.photos || []).map((p, i) => (
          <div key={i} className={`bg-stone-100 rounded-lg overflow-hidden ${i===0?"sm:col-span-3":"aspect-[4/3]"}`}>
            <img src={fileUrl(p)} alt={`${h.name} ${i+1}`} className={i===0 ? "w-full h-auto" : "w-full h-full object-cover"}/>
          </div>
        ))}
        {(!h.photos || h.photos.length === 0) && (
          <div className="sm:col-span-3 aspect-[16/8] bg-stone-100 rounded-lg flex items-center justify-center text-stone-500">Photos à venir</div>
        )}
      </div>

      <p className="text-stone-700 leading-relaxed mb-8" data-testid="hut-desc">{h.description}</p>

      <div className="grid gap-6 md:grid-cols-2">
        <InfoBlock icon={<Users/>} title="Capacité">
          <p>{h.max_capacity} personnes maximum · {h.beds} couchages</p>
        </InfoBlock>
        <InfoBlock icon={<Flame/>} title="Chauffage">
          <p>{h.heating || "Informations à venir"}</p>
        </InfoBlock>
        <InfoBlock icon={<BedDouble/>} title="Équipements">
          {h.equipment && h.equipment.length ? (
            <ul className="list-disc pl-5 space-y-0.5 text-sm">{h.equipment.map((e,i)=><li key={i}>{e}</li>)}</ul>
          ) : <p className="text-sm text-stone-500">Informations à venir</p>}
        </InfoBlock>
        <InfoBlock icon={<MapPin/>} title="Accès & parking">
          <p className="text-sm">{h.parking_info || "—"}</p>
          <div className="mt-2 space-x-2 text-sm">
            {h.parking_map_url && <a href={h.parking_map_url} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">Parking (Google Maps)</a>}
            {h.hut_map_url && <a href={h.hut_map_url} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">Hutte (Google Maps)</a>}
          </div>
        </InfoBlock>
      </div>

      {h.practical_info && (
        <div className="mt-8 p-5 rounded-xl bg-emerald-50 border border-emerald-100">
          <h3 className="font-serif text-xl text-emerald-900 mb-2">Informations pratiques</h3>
          <p className="text-sm text-stone-700 whitespace-pre-wrap">{h.practical_info}</p>
        </div>
      )}
      {h.rules_summary && (
        <div className="mt-4 p-5 rounded-xl bg-stone-50 border border-stone-200">
          <h3 className="font-serif text-xl text-emerald-900 mb-2">Règles à respecter</h3>
          <p className="text-sm text-stone-700 whitespace-pre-wrap">{h.rules_summary}</p>
        </div>
      )}
      {(h.cadastral_photo_url || h.aerial_photo_url) && (
        <div className="mt-8">
          <h3 className="font-serif text-xl text-emerald-900 mb-3">Limites de la zone de chasse</h3>
          <div className="p-3 mb-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded-md flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0"/>
            <span>Les limites indiquées sur ce plan doivent impérativement être respectées. Il est interdit de dépasser les limites de la zone autorisée.</span>
          </div>
          <img src={fileUrl(h.cadastral_photo_url || h.aerial_photo_url)} alt="Limites cadastrales de la zone de chasse" className="w-full rounded-lg border" data-testid="cadastral-photo"/>
        </div>
      )}

      <div className="mt-10 flex justify-center">
        <Link to={`/reserver?hut=${h.slug}`} data-testid="hut-book-btn"
          className={`px-6 py-3 rounded-md font-semibold ${disabled ? "bg-stone-300 text-stone-500 cursor-not-allowed pointer-events-none" : "bg-amber-600 hover:bg-amber-700 text-white"}`}>
          {disabled ? "Réservation prochainement disponible" : `Réserver une nuit — ${h.price_per_night} €`}
        </Link>
      </div>
    </div>
  );
}

function InfoBlock({ icon, title, children }) {
  return (
    <div className="p-5 bg-white rounded-xl border border-stone-200">
      <div className="flex items-center gap-2 mb-2 text-emerald-800">{icon}<h3 className="font-semibold">{title}</h3></div>
      <div className="text-stone-700">{children}</div>
    </div>
  );
}
